import {useDispatch, useSelector} from 'react-redux';
import {hasOneOfPermission, Permissions, user} from '../../../store/reducers/user';
import {useTranslation} from 'react-i18next';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Divider, FormControlLabel, Grid, Paper, Switch} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {additionalData, updatePatientAdditionalData} from '../../../store/reducers/additionalData';
import InputAdornment from '@mui/material/InputAdornment';
import moment from 'moment';
import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import {addNoteForPatient} from '../../../store/reducers/notes';
import MessageBox from '../../../util/messageBox';
import API from '../../../app/api';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import useOpenState from '../../../hooks/useOpenState';
import Chip from '@mui/material/Chip';
import InformationBox from '../../../components/other/informationBox';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {cmToFeetInch} from './formsUtil';
import {getAdmissionStartDate} from '../../../util/patientUtil';
import {getConfig} from '../../../util/configUtil';
import AgyleSelectField from '../../../components/ui/selector/agyleSelectField';
import MiniFormDialog from '../../../components/modal/miniFormDialog';
import {AgyleTable, createAgyleTableColumn} from '../../../components/table/AgyleTable';
import {setSnackbar} from '../../../store/reducers/globals';

const useStyles = makeStyles((theme) => ({
    divider: {
        width: '100%',
        marginTop: 10,
        marginBottom: 15,
    },
    unitLabel: {
        fontSize: 'large',
        paddingRight: 10,
    },
    scoreBody: {
        marginTop: 10,
        padding: 10,
    },
    bmi: {
        fontSize: 'x-large',
        fontWeight: 'bold',
        verticalAlign: 'sub',
        marginLeft: 5,
    },
    lastUpdate: {
        fontSize: 'small',
        fontStyle: 'italic',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
}));

const UNITS = [
    {name: 'Metric', id: 'si'},
    {name: 'Imperial', id: 'imperial'},
];

const NutritionScreen = (props) => {
        const {t} = useTranslation();
        const classes = useStyles();
        const dispatch = useDispatch();

        const age = moment(getAdmissionStartDate(props.patient?.admission)).diff(props.patient?.patient?.dob, 'years');

        // Selectors
        const userObject = useSelector(user);
        const patientAddData = useSelector(additionalData);
        const userFullName = Formatter.formatUserFullName(userObject);

        // Form State
        const [form, setForm] = useState({
            weightSI: '',
            weightStone: '',
            weightPounds: '',
            heightSI: '',
            heightFeet: '',
            heightInches: '',
            muac: '',
        });
        const firstUpdate = useRef(true);

        const [unit, setUnit] = useState('si');
        const [list, setList] = useState([]);
        const [calBMI, setCalBMI] = useState(null);
        const [isHistoryOpen, openHistory, closeHistory] = useOpenState(false, true);
        const [showAll, setShowAll] = useState(true);

        // Message Box
        const [confirmClear, setConfirmClear] = useState(false);

        // Set Object to cancel
        const [cancelNutritionObj, setCancelNutritionObj] = useState(null);

        const canEdit = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_MANAGE_NUTRTION]));

        // Configs
        const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

        useEffect(() => {
            if (patientAddData?.other?.nutritionScreen) {
                setList(patientAddData.other.nutritionScreen);
            }
        }, []);

        useEffect(() => {
            if (props.selectedHistoryForm) openHistory();
        }, [props.selectedHistoryForm]);

        useEffect(() => {
            if (props.onCustomSubmitText) {
                props.onCustomSubmitText(t(renderButtonText()));
            }
            if (props.onDisableSubmit) {
                props.onDisableSubmit(!disabledButton() || !canEdit || props.disableSave);
            }
            if (props.onSubmitFunction) {
                props.onSubmitFunction(calculateBMI);
            }
        }, [form, age, unit, canEdit, props.disableSave]);

        useEffect(() => {
            if (firstUpdate.current && list.length > 0) {
                const filteredList = _.filter(list, el => !el.canceled);
                const sortList = _.orderBy(filteredList, element => {
                    return moment(element.date);
                }, ['desc']);
                const lastItem = _.first(sortList);
                if (lastItem?.height) {
                    if (unit === 'si') {
                        setForm({
                            heightSI: Number(lastItem.height).toFixed(1),
                        });
                    } else {
                        setForm({
                            heightFeet: (lastItem.height / 30.48).toFixed(1),
                            heightInches: ((lastItem.height % 30.48) / 2.54).toFixed(1),
                        });
                    }
                }
                firstUpdate.current = false;
            }
        }, [list, form]);
        useEffect(() => {
            if (!firstUpdate.current) {
                if (unit === 'si') {
                    setForm(prevForm => ({
                        ...prevForm,
                        heightSI: prevForm.heightSI || (prevForm.heightFeet * 30.48 + prevForm.heightInches * 2.54).toFixed(1),
                        weightSI: prevForm.weightSI || (prevForm.weightStone * 6.3503 + prevForm.weightPounds * 0.4536).toFixed(1),
                        heightFeet: '',
                        heightInches: '',
                        weightStone: '',
                        weightPounds: '',
                    }));
                } else {
                    setForm(prevForm => ({
                        ...prevForm,
                        heightFeet: prevForm.heightSI ? Math.floor(prevForm.heightSI / 30.48) : '',
                        heightInches: prevForm.heightSI ? ((prevForm.heightSI % 30.48) / 2.54).toFixed(1) : '',
                        weightStone: prevForm.weightSI ? Math.floor(prevForm.weightSI / 6.3503) : '',
                        weightPounds: prevForm.weightSI ? ((prevForm.weightSI % 6.3503) / 0.4536).toFixed(1) : '',
                        heightSI: '',
                        weightSI: '',
                    }));
                }
            }
        }, [unit]);

        useEffect(() => {
            const buttons = [];
            buttons.push(
                <AgyleButton
                    key="history"
                    colour="primary"
                    onClick={openHistory}
                >
                    {t('Show Patient History')}
                </AgyleButton>,
            );
            if (props.onCustomButtons) {
                props.onCustomButtons(buttons);
            }
        }, [form, unit, list, isHistoryOpen, showAll]);

        const handleChange = (event) => {
            const {name, value} = event.target;
            setForm(prevState => ({...prevState, [name]: value}));
        };

        const handleCloseDialog = () => {
            setConfirmClear(false);
        };

        const disabledButton = () => {
            if (unit === 'si') {
                return form.muac !== '' || form.weightSI !== '' || form.heightSI !== '';
            }
            return form.muac !== '' || (form.weightStone !== '' && form.weightPounds !== '') || (form.heightFeet !== '' && form.heightInches !== '');
        };

        const clearInput = () => {
            setForm({
                weightSI: '',
                weightStone: '',
                weightPounds: '',
                heightSI: '',
                heightFeet: '',
                heightInches: '',
                muac: '',
            });
        };

        // Saving Clinical Notes
        const createNote = (bmi, refId) => {
            dispatch(addNoteForPatient(props.patient.patient.__id, {
                note: Notes.getNutritionScreenNote(form, age, unit, bmi, calculateScoreText(bmi)),
                noteType: 'nutritionScreen',
                patientsInWardsRef: [props.patient.__id],
                referenceId: refId,
                episodeId: props?.patient?.admission?.consultantEpisodeId || null,
            }, props.patient.__id));
        };

        // Creating object to save
        // Convert weight and height to SI units
        const creatObj = (bmi) => {
            firstUpdate.current = false;
            const dateOfForm = moment().toISOString();

            // convert stone and pound to kg
            const uniformWeight = unit === 'si' ? form.weightSI : (form.weightStone * 6.350 + form.weightPounds * 0.4536).toFixed(1);
            // convert feet and inch to cm
            const uniformHeight = unit === 'si' ? form.heightSI : (form.heightFeet * 30.48 + form.heightInches * 2.54).toFixed(1);
            // convert feet to cm
            const uniformMuac = unit === 'si' ? form.muac : (form.muac * 2.54).toFixed(1);

            const obj = {
                id: uuidv4() + '',
                date: dateOfForm,
                by: userFullName,
                login: userObject?.userName,
                attendanceId: props.patient?.admission?.__id,
                bmi: bmi,
                bmiResult: calculateScoreText(bmi),
                weight: uniformWeight !== '0.0' ? uniformWeight : '',
                height: uniformHeight !== '0.0' ? uniformHeight : '',
                muac: uniformMuac !== '0.0' ? uniformMuac : '',
            };

            createNote(bmi, obj.id);

            const listClone = _.cloneDeep(list);
            listClone.push(obj);

            setList(listClone);
            const data = {
                'nutritionScreen': listClone,
            };
            dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));
        };

        // Estimate BMI by weight and height
        const BMINormal = () => {
            let height;
            let weight;

            if (unit === 'si') {
                height = form.heightSI / 100;
                weight = form.weightSI;
            } else {
                height = (form.heightFeet * 0.3048) + (form.heightInches * 0.0254);
                weight = (form.weightStone * 6.3503) + (form.weightPounds * 0.4536);
            }

            const bmi = Number(weight / Math.pow(height, 2)).toFixed(1);
            setCalBMI(bmi);
            creatObj(bmi);
            clearInput();
        };

        // Estimate BMI by MUAC
        const BMIMuac = () => {
            const muac = form.muac;
            const bmi = Number(muac < 23.5 ? 18 : muac > 32 ? 30 : 20).toFixed(1);
            setCalBMI(bmi);
            creatObj(bmi);
            clearInput();
        };

        // Save record without calculating BMI
        const saveRecord = () => {
            setCalBMI(null);
            creatObj(null);
            clearInput();
        };

        const calculateBMI = () => {
            if (age < 18) {
                saveRecord();
            } else {
                if (!_.isNil(form.muac)) {
                    BMIMuac();
                } else if ((form.heightSI !== '' && form.weightSI !== '')
                    || (form.heightFeet !== '' && form.heightInches !== '' && form.weightStone !== '' && form.weightPounds !== '')) {
                    BMINormal();
                } else {
                    saveRecord();
                }
            }
        };

        // Return description to BMI score
        const calculateScoreText = (bmi) => {
            if (!bmi) return '-';

            let bmiScore;
            if (bmi < 18.5) {
                bmiScore = 'Underweight';
            } else if (bmi >= 18.5 && bmi < 25) {
                bmiScore = 'Normal weight';
            } else if (bmi >= 25 && bmi < 30) {
                bmiScore = 'Pre-obesity';
            } else if (bmi >= 30 && bmi < 35) {
                bmiScore = 'Obesity class I';
            } else if (bmi >= 35 && bmi < 40) {
                bmiScore = 'Obesity class II';
            } else if (bmi >= 40) {
                bmiScore = 'Obesity class III';
            }

            return bmiScore;
        };

        // Find when was saved last entry
        const showInformationAboutLastNutrition = (list) => {
            if (!_.isEmpty(list)) {
                const lastItem = _.last(_.filter(list, el => !el.canceled));
                if (lastItem) {
                    const height = heightTable(lastItem);
                    const weight = weightTable(lastItem);
                    let text = '';
                    text += `[${moment(lastItem.date).format(defaultDateTimeFormat)}]`;
                    text += ` by ${lastItem.by},`;
                    text += ` Weight: ${weight}, Height: ${height}`;
                    text += age >= 18 ? `, BMI: ${lastItem?.bmi ? lastItem?.bmi : 'NR'}` : '';

                    return text;
                }
            }
            return 'No entry has been found.';
        };

        const cancelNutrition = () => {
            if (cancelNutritionObj) {
                setConfirmClear(false);
                firstUpdate.current = false;
                const cancelObj = {
                    ...cancelNutritionObj,
                    canceled: true,
                };

                let listClone = _.cloneDeep(list);
                const index = _.findIndex(listClone, {id: cancelObj.id});

                listClone.splice(index, 1, cancelObj);

                setList(listClone);

                const note = Notes.getCancelNutritionScreenNote(cancelObj, age);

                const id = encodeURIComponent(cancelObj.id);
                API.cancelClinicalNoteByRefId(props.patient, id)
                    .finally(() => {
                        dispatch(addNoteForPatient(props.patient.patient.__id, {
                            note: note,
                            noteType: 'nutritionScreen',
                            patientsInWardsRef: [props.patient.__id],
                            episodeId: props?.patient?.admission?.consultantEpisodeId || null,
                        }, props.patient.__id));

                        setCancelNutritionObj(null);

                        const data = {
                            'nutritionScreen': listClone,
                        };
                        dispatch(updatePatientAdditionalData(props.patient.patient.__id, data));
                    });
            }
        };

        function heightTable(element, short) {
            const nr = short ? 'NR' : 'Not Recorded';
            if (unit === 'si') {
                return !_.isEmpty(element.height) ? `${Number(element.height).toFixed(1)} cm` : nr;
            } else {
                return !_.isEmpty(element.height) ? cmToFeetInch(element.height) : nr;
            }
        }

        function weightTable(element, short) {
            const nr = short ? 'NR' : 'Not Recorded';
            if (unit === 'si') {
                return !_.isEmpty(element.weight) ? `${Number(element.weight).toFixed(1)} kg` : nr;
            } else {
                return !_.isEmpty(element.weight) ? `${(element.weight / 0.45359237).toFixed(1)} lb` : nr;
            }
        }

        function muacTable(element, short) {
            const nr = short ? 'NR' : 'Not Recorded';
            if (unit === 'si') {
                return !_.isEmpty(element.muac) ? `${Number(element.muac).toFixed(1)} cm` : nr;
            } else {
                return !_.isEmpty(element.muac) ? `${(element.muac / 2.54).toFixed(1)} in` : nr;
            }
        }

        const renderTable = () => {
            const filteredList = showAll ? list : _.filter(list, el => !el.canceled);
            const sortList = _.orderBy(filteredList, element => {
                return moment(element.date);
            }, ['desc']);

            if (list && list.length > 0) {
                const handleDelete = (row) => {
                    if (row?.canceled) {
                        dispatch(setSnackbar({type: 'warning', message: t('This record has already been canceled.')}));
                        return null;
                    } else {
                        setCancelNutritionObj(row);
                        setConfirmClear(true);
                    }
                };

                const COLUMNS = [
                    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, 50, null),
                    createAgyleTableColumn('height', 'Height', 2, null, 'transformCustomData', '', 'medium', 'center', true, true, null, 140, {transformVal: heightTable}),
                    createAgyleTableColumn('weight', 'Weight', 3, null, 'transformCustomData', '', 'medium', 'center', true, true, 150, null, {transformVal: weightTable}),
                    createAgyleTableColumn('muac', 'MUAC', 4, null, 'transformCustomData', '', 'medium', 'center', true, true, 115, null, {datatransformVal: muacTable}),
                    createAgyleTableColumn('bmi', 'BMI', 5, 'bmi', 'string', '', 'medium', 'center', true, true, null, null),
                    createAgyleTableColumn('bmiResult', 'BMI Result', 6, 'bmiResult', 'string', '', 'medium', 'center', true, true, null, null),
                    createAgyleTableColumn('date', 'Date', 7, 'date', 'date', '', 'medium', 'center', true, true, null, null),
                    createAgyleTableColumn('by', 'Create by', 8, 'by', 'string', '', 'medium', 'center', true, true, null, null),
                    createAgyleTableColumn('action', 'Action', 9, null, 'action', 'notSort', 'medium', 'right', true, true, null, null),
                ];

                return (
                    <AgyleTable
                        columns={COLUMNS}
                        data={sortList}
                        deleteItem={handleDelete}
                        disableFilter
                        disabledAction={props.disableSave}
                        cancelFiled="canceled"
                        highlightRow={props.selectedHistoryForm}
                    />
                );
            }

            return (
                <h1 style={{textAlign: 'center'}}>
                    {t('No entry has been found.')}
                </h1>
            );
        };

        const renderInput = () => {
            return (
                <>
                    {unit === 'si' &&
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <AgyleTextField
                                    label={t('Weight')}
                                    disabled={!canEdit}
                                    name="weightSI"
                                    onChange={handleChange}
                                    type="number"
                                    value={form.weightSI}
                                    endAdornment={<InputAdornment position="end">kg</InputAdornment>}
                                    size="large"
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <AgyleTextField
                                    label={t('Height')}
                                    disabled={!canEdit}
                                    name="heightSI"
                                    onChange={handleChange}
                                    type="number"
                                    value={form.heightSI}
                                    endAdornment={<InputAdornment position="end">cm</InputAdornment>}
                                    size="large"
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>
                        </Grid>
                    }

                    {unit === 'imperial' &&
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <AgyleTextField
                                    label={t('Weight')}
                                    disabled={!canEdit}
                                    name="weightStone"
                                    onChange={handleChange}
                                    type="number"
                                    value={form.weightStone}
                                    endAdornment={<InputAdornment position="end">st</InputAdornment>}
                                    size="large"
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <AgyleTextField
                                    label={t('Weight')}
                                    disabled={!canEdit}
                                    name="weightPounds"
                                    onChange={handleChange}
                                    type="number"
                                    value={form.weightPounds}
                                    endAdornment={<InputAdornment position="end">lb</InputAdornment>}
                                    size="large"
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <AgyleTextField
                                    label={t('Height')}
                                    disabled={!canEdit}
                                    name="heightFeet"
                                    onChange={handleChange}
                                    type="number"
                                    value={form.heightFeet}
                                    endAdornment={<InputAdornment position="end">ft</InputAdornment>}
                                    size="large"
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <AgyleTextField
                                    label={t('Height')}
                                    disabled={!canEdit}
                                    name="heightInches"
                                    onChange={handleChange}
                                    type="number"
                                    value={form.heightInches}
                                    endAdornment={<InputAdornment position="end">in</InputAdornment>}
                                    size="large"
                                    fullWidth
                                    noBorder={false}
                                    withNewBackground
                                />
                            </Grid>

                        </Grid>
                    }

                    <Divider className={classes.divider} sx={{pt: 1}}/>

                    <Grid item xs={12} sx={{mb: 1}}>
                        <AgyleTextField
                            label={t('Mid Upper Arm Circumference')}
                            disabled={!canEdit}
                            name="muac"
                            onChange={handleChange}
                            type="number"
                            value={form.muac}
                            endAdornment={<InputAdornment
                                position="end">{unit === 'si' ? 'cm' : 'in'}</InputAdornment>}
                            size="large"
                            fullWidth
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                </>
            );
        };

        const displayScore = () => {
            const bmiScore = calculateScoreText(calBMI);

            return (
                <Paper className={classes.scoreBody}>
                    <Grid item xs={12}>
                        <Chip
                            label={t('Patient BMI')}
                            variant="filled"
                        />
                        <span className={classes.bmi}>
                            {calBMI}
                        </span>
                    </Grid>

                    <Grid item xs={12}>
                        {t('BMI suggests patient results are:')}{' '}
                        <span style={{fontWeight: 'bold'}}>
                            {bmiScore}
                        </span>
                    </Grid>
                </Paper>
            );
        };

        const renderUnitControl = () => {
            return (
                <div className={classes.divRow}>
                    <div className={classes.unitLabel}>
                        {t('Units')}
                    </div>

                    <AgyleSelectField
                        onChange={setUnit}
                        options={UNITS}
                        value={unit}
                        withBackground
                        shrink
                        defaultClearValue="si"
                        withNewBackground
                        withBorder
                    />
                </div>
            );
        };

        const renderButtonText = () => {
            if (age >= 18) {
                if (unit === 'si') {
                    if (form.muac !== '' || (form.heightSI !== '' && form.weightSI !== '')) {
                        return `Add entry to history and calculate BMI`;
                    }
                } else {
                    if (form.muac !== '' || (form.heightFeet !== '' && form.heightInches !== '' && form.weightStone !== '' && form.weightPounds !== '')) {
                        return `Add entry to history and calculate BMI`;
                    }
                }
            }

            return `Add entry to history`;
        };

        const handleCloseHistory = (_e, reason) => {
            if (reason !== 'backdropClick') {
                closeHistory();
                if (props.selectedHistoryForm && props._onForceClose)
                    props._onForceClose();
            }
        };

        return (
            <>
                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                    <div className={classes.lastUpdate}>
                        <Chip
                            sx={{mr: 1, mb: 1}}
                            label={t(`Last Update: ${showInformationAboutLastNutrition(list)}`)}
                            variant="filled"
                        />
                    </div>

                    {renderUnitControl()}
                </Grid>

                <InformationBox type={'info'}>
                    {t('Please enter weight or height to save record.')}
                    <br/>
                    {age >= 18 && t('To calculate BMI, provide weight and height or MUAC (alternative measurement if it is not possible to weigh the patient).')}
                </InformationBox>

                <Grid container sx={{my: 2}}>
                    {renderInput()}
                </Grid>

                {calBMI && displayScore()}

                {/* Patient History */}
                <MiniFormDialog
                    title={age >= 18 ? t('Weight / Height / BMI - Patient History') : t('Weight / Height - Patient History')}
                    open={isHistoryOpen}
                    onClose={handleCloseHistory}
                    width="lg"
                    disableSave
                >
                    <Grid container direction="row" justifyContent="space-around">
                        <Grid item xs={6}>
                            {renderUnitControl()}
                        </Grid>

                        <Grid item xs={6}>
                            <FormControlLabel
                                style={{float: 'right'}}
                                control={
                                    <Switch checked={showAll} onChange={() => setShowAll(prev => !prev)}/>
                                }
                                label={t('Show All')}
                            />
                        </Grid>
                    </Grid>

                    {renderTable()}
                </MiniFormDialog>

                <MessageBox
                    onClose={handleCloseDialog}
                    title={t('Confirmation')}
                    message={t('Are you sure you want to cancel this record?')}
                    haveAccept={{yes: t('Submit'), no: t('Close')}}
                    onAccept={cancelNutrition}
                    open={confirmClear}
                />
            </>
        );
    }
;

export default NutritionScreen;